import _ from "lodash";
import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { connect } from "react-redux";
import axios from "axios";
import PropTypes from "prop-types";

import { OneDriveColored } from "@toddle-edu/ds-icons";
import {
  generateRandomId,
  getFileType,
  getFileName,
  getImageDimensions,
  getMaxSize,
  getMaxSizeForZipFiles,
  getMaxSizeForVideoFiles,
  getUpdatedMaxSize,
  getFileTypesAndMaxSizes,
  getFileExtensionFromMimeType,
  getMediaCardFileType,
} from "Utils";
import {
  getFileExtensionFromFilename,
  getSharepointFileDetails,
  ARCHIVE_MIME_TYPES,
  getMimeTypeFromFileName,
} from "Utils/AttachmentHelpers";
import {
  PICKER_DIRECT_DOWNLOADABLE_TYPES,
  BLOCKED_FILE_EXTENSIONS,
  BLOCKED_FILE_MIME_TYPES,
} from "Constants";

import ACLStore from "lib/aclStore";
import { Sentry } from "services/sentry";
import {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  exceededSizeError,
  heicToJpg,
  unsupportedFileExtensionError,
} from "modules/Services";

import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import DriveShareModal from "UIComponents/DriveShareModal";
import Drive from "UIComponents/MicrosoftPicker/Drive";
import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import SharePointPickerIframeModal from "./components/SharePointPickerIframeModal";
import { constructPath } from "../modules/Utils";
import { is500MBVideoUploadLimitEnabled } from "Post/utils";

const SharepointPickerV2 = forwardRef(
  (
    {
      parentId,
      parentType,
      sourceType,
      defaultExportType,
      attachmentExportOptions,
      multiSelect,
      enableShareModal,
      shouldUpload,
      exportAsPdf,
      maxSize,
      portalType,
      renderComponent,
      classStyle,
      setToastMsg,
      downloadFromUrl,
      onContentUploaded,
      onSingleContentUploaded,
      onMultipleFilesUploaded,
      uploadFile,
      onContentLoaded,
      onDropContent,
      removeProgressElement,
      updateFilesCount,
      exceededSizeError,
      heicToJpg,
      accept,
      excludeFileExtensionsList,
      unsupportedFileExtensionError,
      customFilterFilesHandler,
      userType,
      maxSizeForVideoFiles,
      maxSizeForZipFiles,
      shouldReturnSignedUrl,
      isIncreasedUploadLimit,
    },
    ref
  ) => {
    const [isPickerLoading, setIsPickerLoading] = useState(true);
    const [showPickerModal, setShowPickerModal] = useState(false);
    const [showDrivePicker, setShowDrivePicker] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [graphAccessToken, setGraphAccessToken] = useState("");
    const [pickerAccessToken, setPickerAccessToken] = useState("");
    const [teamsAccessToken, setTeamsAccessToken] = useState("");
    const [siteRootUrl, setsiteRootUrl] = useState("");
    const [files, setFiles] = useState([]);

    const iframeRef = useRef(null);
    const authPopUpRef = useRef();

    const channelIdRef = useRef(generateRandomId());
    const channelId = channelIdRef.current;

    useEffect(() => {
      window.addEventListener("message", messageEventListener);
      return () => {
        window.removeEventListener("message", messageEventListener);
      };
    });

    const disablePicker = useCallback(() => {
      setShowPickerModal(false);
      setShowDrivePicker(false);
      setIsPickerLoading(true);
    }, [setShowPickerModal, setShowDrivePicker, setIsPickerLoading]);

    const launchPicker = e => {
      disablePicker(); //disable modals before launching
      e?.stopPropagation?.();
      if (authPopUpRef.current) {
        const onAuthClick = _.get(
          authPopUpRef,
          "current.refs.wrappedInstance.onAuthClick"
        );
        onAuthClick();
      }
    };

    useImperativeHandle(ref, () => ({
      launchSharepointPicker: launchPicker,
    }));

    const getFileFormats = ({ accept, excludeFileExtensionsList }) => {
      const supportedFileFormats = _.uniq(
        _.compact(
          _.map(accept, allowedType => {
            let fileExtension = getFileExtensionFromMimeType({
              mimeType: allowedType,
            });

            // Handle special case for "qt" files
            if (
              fileExtension === "qt" &&
              _.includes(excludeFileExtensionsList, ".qt")
            ) {
              fileExtension = "mov";
            }

            return fileExtension ? `.${fileExtension}` : null;
          })
        )
      );
      return supportedFileFormats;
    };

    const generateBaseUrl = useCallback(() => {
      const params = {
        sdk: "8.0",
        entry: {
          oneDrive: {},
        },
        authentication: {},
        selection: {
          mode: multiSelect ? "multiple" : "single",
          enablePersistence: true,
        },
        typesAndSources: {
          mode: "files",
          filters: getFileFormats({ accept, excludeFileExtensionsList }),
        },
        messaging: {
          origin: `${window.location.origin}`,
          channelId: channelId,
        },
      };
      const queryString = new URLSearchParams({
        filePicker: JSON.stringify(params),
      });
      const url = constructPath(
        siteRootUrl,
        `/_layouts/15/FilePicker.aspx?${queryString}`
      );
      return url;
    }, [
      multiSelect,
      accept,
      excludeFileExtensionsList,
      channelId,
      siteRootUrl,
    ]);

    const submitOnMount = useCallback(form => {
      if (!form) return;
      form.submit();
    }, []);

    const messageEventListener = event => {
      if (
        event.source &&
        iframeRef.current &&
        event.source === iframeRef.current.contentWindow
      ) {
        const message = event.data;

        if (message.type === "initialize" && message.channelId === channelId) {
          const port = event.ports[0];

          port.addEventListener("message", message => {
            switch (message.data.type) {
              case "notification":
                break;

              case "command":
                port.postMessage({
                  type: "acknowledge",
                  id: message.data.id,
                });

                // eslint-disable-next-line no-case-declarations
                const command = message.data.data;

                switch (command.command) {
                  case "authenticate":
                    // eslint-disable-next-line no-case-declarations
                    let token = pickerAccessToken;
                    if (
                      !_.isEmpty(teamsAccessToken) &&
                      command.type !== "SharePoint" &&
                      command.type !== "SharePoint_SelfIssued"
                    ) {
                      token = teamsAccessToken;
                    }
                    if (typeof token !== "undefined" && token !== null) {
                      port.postMessage({
                        type: "result",
                        id: message.data.id,
                        data: {
                          result: "token",
                          token,
                        },
                      });
                    } else {
                      console.error(
                        `Could not get auth token for command: ${JSON.stringify(
                          command
                        )}`
                      );
                    }
                    break;

                  case "close":
                    disablePicker();
                    break;

                  case "pick":
                    port.postMessage({
                      type: "result",
                      id: message.data.id,
                      data: {
                        result: "success",
                      },
                    });
                    onSucces(command);
                    disablePicker();
                    break;

                  default:
                    console.warn(
                      `Unsupported command: ${JSON.stringify(command)}`,
                      2
                    );

                    port.postMessage({
                      result: "error",
                      error: {
                        code: "unsupportedCommand",
                        message: command.command,
                      },
                      isExpected: true,
                    });
                    break;
                }
                break;
            }
          });

          port.start();

          port.postMessage({
            type: "activate",
          });
        }
      }
    };

    const onSucces = data => {
      getFilesInfo({
        files: _.get(data, "items", []),
        accessToken: graphAccessToken,
      });
    };

    const getFilesInfo = async ({ files: selectedFiles, accessToken }) => {
      try {
        //creating requests to fetch selected files info
        const filesInfo = _.map(selectedFiles, selectedFile => {
          const driveId = _.get(selectedFile, "parentReference.driveId", "");
          const itemId = _.get(selectedFile, "id", "");
          return axios.get(
            `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
        });

        //fetching information for selected files
        const files = _.map(
          _.filter(
            await Promise.all(filesInfo),
            responseObj => responseObj?.data
          ),
          fileResponse => {
            const fileInfo = fileResponse.data;
            return {
              downloadUrl: fileInfo["@microsoft.graph.downloadUrl"],
              url: fileInfo.webUrl,
              type: getMediaCardFileType({
                type: "FILE",
                mimeType: _.get(fileInfo, "file.mimeType", ""),
              }),
              mimeType: _.get(fileInfo, "file.mimeType", ""),
              size: fileInfo.size,
              name: fileInfo.name,
              id: fileInfo.id,
              driveId: _.get(fileInfo, "parentReference.driveId", ""),
            };
          }
        );

        //checking if files are in allowed file formats
        // Note: extension is taken from the mimeType
        let allowedFiles = _.isEmpty(accept)
          ? files
          : files.filter(file => _.includes(accept, file.mimeType));

        const notAllowedFileTypeCount = _.size(files) - _.size(allowedFiles);
        if (notAllowedFileTypeCount > 0) {
          onNotAllowedFileType({
            notAllowedFileCount: notAllowedFileTypeCount,
            fileFormats: getFileFormats({ accept, excludeFileExtensionsList }),
          });
        }

        // filtering files on basis of excludedFileExtensionList
        // Note: extension is taken from the fileName
        const blockMaliciousMimeTypes = ACLStore.can(
          "FeatureFlag:BlockMaliciousMimeType"
        );
        const blockedFileExtensions = blockMaliciousMimeTypes
          ? _.union(excludeFileExtensionsList, BLOCKED_FILE_EXTENSIONS)
          : excludeFileExtensionsList;
        const blockedFileMimeTypes = blockMaliciousMimeTypes
          ? BLOCKED_FILE_MIME_TYPES
          : [];
        const notAllowedFileExtensions = [];
        allowedFiles = _.filter(allowedFiles, file => {
          const mimeType =
            file?.mimeType || getMimeTypeFromFileName({ fileName: file?.name });

          const extension = `.${
            getFileExtensionFromFilename({
              fileName: file?.name,
            }) || getFileExtensionFromMimeType({ mimeType })
          }`;

          if (
            _.includes(blockedFileExtensions, extension) ||
            _.includes(blockedFileMimeTypes, mimeType)
          ) {
            notAllowedFileExtensions.push(extension);
            return false;
          }
          return true;
        });

        if (_.size(notAllowedFileExtensions) > 0) {
          unsupportedFileExtensionError({
            notAllowedFileExtensions: _.uniq(notAllowedFileExtensions),
            notAllowedFileCount: _.size(notAllowedFileExtensions),
          });
        }

        // filter files based on custom logic
        if (customFilterFilesHandler) {
          allowedFiles = customFilterFilesHandler(allowedFiles);
        }

        const fileCount = _.size(allowedFiles);
        if (fileCount > 0) {
          if (enableShareModal) {
            setFiles(allowedFiles);
            setShowShareModal(true);
          } else {
            processChosenFiles({ files: allowedFiles });
          }
        }
      } catch (err) {
        console.error("Error fetching file information:", err);
      }
    };

    const onNotAllowedFileType = ({ notAllowedFileCount, fileFormats }) => {
      const fileFormatCount = _.size(fileFormats);

      const localeParams = [
        {
          key: "fileCount",
          value: notAllowedFileCount,
          isPlainText: true,
        },
        {
          key: "fileFormatCount",
          value: fileFormatCount,
          isPlainText: true,
        },
        {
          key: "entity1",
          value: `"${_.join(_.slice(fileFormats, 0, -1), ", ")}"`,
          isPlainText: true,
        },
        {
          key: "entity2",
          value: `"${_.last(fileFormats)}"`,
          isPlainText: true,
        },
      ];
      setToastMsg({
        msg:
          "toastMsgs:fileCount_file_will_be_not_uploaded_only_fileFormate_format_allowed",
        locale_params: localeParams,
        type: "warning",
        timeOut: 3500,
        position: "toast-top-center",
      });
    };

    const processChosenFiles = ({ files }) => {
      const sendObjList = [];

      //checking file size limit
      let allowedFilesWithSizeLimit = _.filter(files, file => {
        const fileType = file?.mimeType;

        const isLink = enableShareModal
          ? _.isEqual(file.exportAs, "LINK")
          : shouldExportAsLink(file.mimeType);
        if (!isLink) {
          const updatedMaxSize = getUpdatedMaxSize({
            maxSize: _.isArray(maxSize)
              ? maxSize
              : _.max(maxSize, maxSizeForVideoFiles, maxSizeForZipFiles),
            fileType,
          });

          return _.isNumber(updatedMaxSize)
            ? updatedMaxSize >= _.get(file, "size", 0)
            : true;
        }
        return true;
      });

      if (!_.isArray(maxSize)) {
        const oversizedFiles = _.filter(allowedFilesWithSizeLimit, file => {
          const isLink = enableShareModal
            ? _.isEqual(file.exportAs, "LINK")
            : shouldExportAsLink(file.mimeType);
          if (isLink) {
            return false;
          }
          let maxSizeForFile = maxSize;
          if (
            is500MBVideoUploadLimitEnabled() &&
            file?.mimeType?.startsWith("video/")
          ) {
            maxSizeForFile = maxSizeForVideoFiles;
          }

          if (
            isIncreasedUploadLimit &&
            _.includes(ARCHIVE_MIME_TYPES, file?.mimeType)
          ) {
            maxSizeForFile = maxSizeForZipFiles;
          }

          return maxSizeForFile < _.get(file, "size", 0);
        });

        if (!_.isEmpty(oversizedFiles)) {
          allowedFilesWithSizeLimit = _.filter(
            allowedFilesWithSizeLimit,
            file => {
              return !_.some(
                oversizedFiles,
                oversizedFile =>
                  file.name === oversizedFile.name &&
                  file.size === oversizedFile.size &&
                  file.lastModified === oversizedFile.lastModified
              );
            }
          );
        }
      }

      const fileCount = _.size(allowedFilesWithSizeLimit);
      _.forEach(allowedFilesWithSizeLimit, allowedFile => {
        fileDownloadAndUpload({
          file: allowedFile,
          sendObjList,
          fileCount,
        });
      });

      if (_.size(files) > _.size(allowedFilesWithSizeLimit)) {
        const fileTypesAndMaxSizes = getFileTypesAndMaxSizes({
          files,
          allowedFiles: allowedFilesWithSizeLimit,
          fileTypePropName: "mimeType",
          maxSize: maxSize,
          maxSizeForVideoFiles: maxSizeForVideoFiles,
          maxSizeForZipFiles: maxSizeForZipFiles,
          is500MBVideoLimitEnabled: is500MBVideoUploadLimitEnabled(),
          isIncreasedUploadLimit: isIncreasedUploadLimit,
        });

        exceededSizeError({
          fileTypesAndMaxSizes,
        });
      }
    };

    const fileDownloadAndUpload = async ({ file, sendObjList, fileCount }) => {
      updateFilesCount(1);
      const isLink = enableShareModal
        ? _.isEqual(file.exportAs, "LINK")
        : shouldExportAsLink(file.mimeType);
      const fileDetails = await getSharepointFileDetails({
        accessToken: graphAccessToken,
        driveId: _.get(file, "driveId", ""),
        fileId: _.get(file, "id", ""),
      });
      const thumbUrl = _.get(fileDetails, "thumbnailLink");

      const sendObj = await getSendObject(file, isLink);
      //Sending this metadata to for BE to download and upload to S3.
      //Sending this with object as thumbUrl with to show preview while creation.
      if (thumbUrl) {
        sendObj.metadata.tempThumbUrl = thumbUrl;
        sendObj.thumbUrl = thumbUrl;
      }
      const fileUploadId = generateRandomId();
      if (onDropContent) {
        //here we have either thumburl or link url(actual file url) as file url
        onDropContent({ ...sendObj, id: fileUploadId });
      }

      const startProgress = 50;
      if (!isLink) {
        //file is not of type link, i.e may be exportAsPdf is true or it is inside the allowed mmimetype
        //starting the download of file from one drive.
        downloadFromUrl({
          downloadUrl: file.downloadUrl,
          size: file.size,
          name: file.name,
          fileUploadId: fileUploadId,
          parentType: parentType,
          parentId: parentId,
          attachment: sendObj,
          onContentLoadFailed: () => {
            updateFilesCount(-1, true);
          },
          onLoaded: async fileBlob => {
            if (fileBlob) {
              fileBlob.name = _.get(file, "name", "Untitled");
              if (!fileBlob.type) {
                fileBlob.mimeType = _.get(file, "mimeType");
              }
            }
            const sendObj = onFileDownloadedFromDrive({
              file: fileBlob,
              uploadId: fileUploadId,
              startProgress: startProgress,
              mimeType: _.get(file, "mimeType"),
            });
            sendObjList.push(sendObj);

            // Sends all files sendObj together as an array
            handleOnMultipleFileUpload({ sendObjList, fileCount });
          },
        });
      } else {
        if (onContentLoaded) {
          onContentLoaded(sendObj);
        }
        // we have a link of file
        if (onContentUploaded) {
          onContentUploaded(sendObj);
        }
        if (onSingleContentUploaded) {
          onSingleContentUploaded(sendObj, fileUploadId, {
            parentId,
            parentType,
          });
        }

        sendObjList.push(sendObj);
        // Sends all files sendObj together as an array
        handleOnMultipleFileUpload({ sendObjList, fileCount });
        updateFilesCount(-1);
      }
    };

    const handleOnMultipleFileUpload = async ({ sendObjList, fileCount }) => {
      if (_.size(sendObjList) == fileCount && onMultipleFilesUploaded) {
        const values = _.filter(
          await Promise.all(sendObjList),
          urlObj => urlObj
        );
        onMultipleFilesUploaded(values);
      }
    };

    const onFileDownloadedFromDrive = async ({
      file,
      uploadId,
      startProgress,
      mimeType,
    }) => {
      let updatedFile = file;
      if (ACLStore.can("FeatureFlag:EnableHEICConversion")) {
        try {
          updatedFile = await heicToJpg({
            file,
            uploadId,
            startProgress: 0,
            mimeType,
          });
        } catch (e) {
          Sentry.captureMessage("HEIC_CONVERSION_ISSUE_", {
            extra: {
              functionName: "heicToJpg",
              reason: _.get(e, "message"),
              fileType: _.get(file, "type"),
              component: "SharepointPickerV2",
            },
          });
        }
      }
      if (onContentLoaded) {
        //handle your own upload
        const url = URL.createObjectURL(updatedFile);
        updatedFile["downloadUrl"] = url;

        const sendObj = await getSendObject(updatedFile);
        onContentLoaded({ ...sendObj, file: updatedFile, url });
        removeProgressElement({ id: uploadId });
        updateFilesCount(-1);
      } else {
        if (shouldUpload) {
          // uploading asset to our server. only when file has allowed mimetype

          return new Promise((resolve, reject) => {
            getSendObject(updatedFile)
              .then(sendObj => {
                const uploadStartTime = Date.now();
                const { promise } = uploadFile({
                  file: updatedFile,
                  uploadId,
                  startProgress,
                  parentType,
                  parentId,
                  attachment: sendObj,
                  portalType,
                  shouldReturnSignedUrl,
                });
                promise
                  .then(async item => {
                    let sendObj = await getSendObject({
                      ...updatedFile,
                      type: updatedFile.type,
                      size: updatedFile.size,
                      downloadUrl: item,
                    });
                    const uploadTime = Date.now() - uploadStartTime;
                    const metadata = _.get(sendObj, "metadata", {});
                    const updatedMetadata = { ...metadata, uploadTime };
                    sendObj = { ...sendObj, metadata: updatedMetadata };
                    if (onContentUploaded) {
                      await onContentUploaded(sendObj);
                    }
                    if (onSingleContentUploaded) {
                      await onSingleContentUploaded(sendObj, uploadId, {
                        parentId,
                        parentType,
                      });
                    }
                    removeProgressElement({ id: uploadId });
                    updateFilesCount(-1);
                    resolve(sendObj);
                  })
                  .catch(e => {
                    console.error(e);
                    updateFilesCount(-1, true);
                  });
              })
              .catch(reject);
          });
        }
      }
    };

    const getSendObject = async (file, isLink) => {
      const mimeType = file.mimeType ? file.mimeType : file.type;
      const url = isLink ? file.url : file.downloadUrl;
      const attachmentType = isLink ? "LINK" : getFileType(mimeType).text;
      const dimensions = await getImageDimensions({
        url,
        type: attachmentType,
      });
      const metadata = {
        size: _.get(file, "size", 0),
        serviceType: "MICROSOFT",
        fileType:
          _.get(file, "type") ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ? "document"
            : _.get(file, "type"),
        fileId: _.get(file, "id"),
        driveId: _.get(file, "driveId"),
        ...dimensions,
      };
      if (file.permission) {
        metadata.permission = file.permission;
      }
      return {
        type: attachmentType,
        url: url,
        name: getFileName(file.name, mimeType),
        mimeType: mimeType,
        metadata: metadata,
      };
    };

    const shouldExportAsLink = mimeType => {
      if (
        enableShareModal ||
        _.includes(PICKER_DIRECT_DOWNLOADABLE_TYPES, mimeType)
      ) {
        return false;
      }
      return !exportAsPdf;
    };

    const toggleSharingFormatModal = () => {
      setShowShareModal(prev => !prev);
    };

    const disablePickerLoading = useCallback(() => {
      setIsPickerLoading(false);
    }, [setIsPickerLoading]);

    const onChoose = async data => {
      const fileCount = _.size(data);
      if (fileCount > 0) {
        if (enableShareModal) {
          setFiles(data);
          setShowShareModal(true);
        } else {
          processChosenFiles({ files: data });
        }
      }
    };

    const successCallback = data => {
      const {
        graphAccessToken,
        pickerAccessToken,
        teamsAccessToken,
        siteRootUrl,
      } = data;
      //if site root url is empty switch to drive picker
      if (_.isEmpty(siteRootUrl)) {
        setShowDrivePicker(true);
        setGraphAccessToken(graphAccessToken);
        return;
      }
      setGraphAccessToken(graphAccessToken);
      setPickerAccessToken(pickerAccessToken);
      setTeamsAccessToken(teamsAccessToken);
      setsiteRootUrl(siteRootUrl);
      setShowPickerModal(true);
    };

    return (
      <React.Fragment>
        <ThirdPartyAuth
          serviceType={"MICROSOFT"}
          fetchThirdPartyPickerTokens={true}
          ref={authPopUpRef}
          onSuccessCallBack={successCallback}
        />
        {renderComponent ? (
          renderComponent
        ) : (
          <div className={classStyle}>
            <OneDriveColored size="xx-small" variant="subtle" />
          </div>
        )}
        {showDrivePicker && !showShareModal && graphAccessToken && (
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Drive
              multiselect={multiSelect}
              onChoose={onChoose}
              accessToken={graphAccessToken}
              accept={_.uniq(accept)}
              onClose={disablePicker}
              excludeFileExtensionsList={excludeFileExtensionsList}
              customFilterFilesHandler={customFilterFilesHandler}
            />
          </div>
        )}
        {showPickerModal && (
          <SharePointPickerIframeModal
            graphAccessToken={graphAccessToken}
            disablePicker={disablePicker}
            generateBaseUrl={generateBaseUrl}
            submitOnMount={submitOnMount}
            iframeRef={iframeRef}
            disablePickerLoading={disablePickerLoading}
            isPickerLoading={isPickerLoading}
          />
        )}
        {enableShareModal && showShareModal && (
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <DriveShareModal
              serviceType={"MICROSOFT"}
              processChosenFiles={processChosenFiles}
              onClickClose={toggleSharingFormatModal}
              files={files}
              accessToken={graphAccessToken}
              sourceType={sourceType}
              defaultExportType={defaultExportType}
              attachmentExportOptions={attachmentExportOptions}
              customStyle={{ overlay: { zIndex: 1201 } }}
              userType={userType}
            />
          </div>
        )}
      </React.Fragment>
    );
  }
);

SharepointPickerV2.displayName = "SharepointPickerV2";

SharepointPickerV2.prototype = {
  accept: PropTypes.array,
  enableShareModal: PropTypes.bool,
  onContentLoaded: PropTypes.func,
  onContentUploaded: PropTypes.func,
  updateFilesCount: PropTypes.func,
  renderComponent: PropTypes.element,
  multiSelect: PropTypes.bool,
  shouldUpload: PropTypes.bool,
  onSingleContentUploaded: PropTypes.func,
  onDropContent: PropTypes.func,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  exportAsPdf: PropTypes.bool,
  sourceType: PropTypes.oneOf(["RESOURCE", "STUDENT_TEMPLATE"]),
  attachmentExportOptions: PropTypes.array,
  customFilterFilesHandler: PropTypes.func,
};

SharepointPickerV2.defaultProps = {
  sourceType: "RESOURCE",
  parentType: "",
  parentId: null,
  multiSelect: true,
  exportAsPdf: false,
  enableShareModal: true,
  shouldUpload: true,
};

const mapStateToProps = (state, ownProps) => {
  let { maxSize } = ownProps;
  const { isIncreasedUploadLimit = false } = ownProps;

  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const allowInfiniteFileSize = ACLStore.can(
    "FeatureFlag:DropzoneInfiniteFileSize"
  );

  const maxSizeForVideoFiles = getMaxSizeForVideoFiles({
    maxSize,
    isCurriculumProgramFree,
    is500MBVideoLimit: is500MBVideoUploadLimitEnabled(),
    allowInfiniteFileSize,
  });

  const maxSizeForZipFiles = getMaxSizeForZipFiles({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
    isIncreasedUploadLimit,
  });

  maxSize = getMaxSize({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
  });

  return {
    maxSize,
    maxSizeForVideoFiles,
    maxSizeForZipFiles,
    userType: state.login.userInfo.user_type,
    isCurriculumProgramFree,
  };
};

const mapActionCreators = {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  setToastMsg,
  exceededSizeError,
  heicToJpg,
  unsupportedFileExtensionError,
};
export default connect(mapStateToProps, mapActionCreators, null, {
  withRef: true,
})(SharepointPickerV2);
